import { BankRemittanceModelType, BankRemittanceStatusKeys } from '@teinor/erp/types/company/accounting/bankRemittance'
import { useParams } from 'react-router-dom'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import DoubleTable from '../../../../../../../baseComponents/DoubleTable'
import TwinTable, { TwinTableLink } from '../../../../../../../baseComponents/TwinTable'
import { TableComponentProps } from '../../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderDate, CustomRenderDisplayPriceWOParams, EditRow, EditRowNotLink } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import locations from '../../../../../../../utils/globals/locations'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import BaseScreenTable from '../../../../../../../baseComponents/TwinTable/BaseScreenTable'
import NullComponent from '../../../../../../../baseComponents/NullComponent'
import { PayMCopyDateStatusKeys } from '@teinor/erp/types/company/accounting/payMCopy/payMCopyDate'
import { DisplayStatus } from '../../../../../../../baseComponents/Displays/DisplayStatus'
import useCollectionAndPaymentStatus from '../../../../../../../utils/hooks/accounting/useCollectionAndPaymentStatus'
import { useAddDoubleTableGreenRowRender } from '../../../../../../../baseComponents/DoubleTable/functions'
import { ButtonPrimary } from '../../../../../../../baseComponents/Button'
import useIsLoading from '../../../../../../../utils/hooks/useIsLoading'
import { useCallback } from 'react'
import twinFetchPost from '../../../../../../../utils/globals/data'
import LoadingSpinner from '../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import ModalNewRemittance from './ModalNewRemittance'
import useBankRemittanceStatus from '../../../../../../../utils/hooks/accounting/useBankRemittanceStatus'
import ModalEditRemittance from './ModalEditRemittance'
import ModalEditPaymentMethodDate from '../ModalEditPaymentMethodDate'
import { changeErrorMessage } from '../../../../../../../utils/reducers/reduxDispatch'
import { PayMCopyDateModelTypeComplete } from '../ModalMarkRemittanceAsPaid/ModalSelectRemittanceAsPaid/types'

interface RemittancesProps extends ComponentWithPermissions { }

const Remittances: React.FC<RemittancesProps> = ({ userPermissions }) => {
    const { id } = useParams()

    return (
        <DoubleTable FirstChild={RemittancesInner} SecondChild={RemittancesPaymentsTable} userPermissions={userPermissions} displaySecond={id !== 'main'} />
    )
}

interface RemittancesInnerProps extends ComponentWithPermissions { }

const RemittancesInner: React.FC<RemittancesInnerProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()
    const pageData = {
        title: t('remittances', 'Remesas'),
        newText: t('newRemittance', 'Nueva remesa'),
    }

    return (
        <BaseScreenTable TableComponent={TableRemittances} CreateModalComponent={ModalNewRemittance} EditModalComponent={ModalEditRemittance} pageData={pageData} userPermissions={userPermissions} />
    )
}


interface TableRemittancesProps extends TableComponentProps { }

const TableRemittances: React.FC<TableRemittancesProps> = ({ setTableInstance, setOpened }) => {
    const { t } = useTwinTranslation()
    const { id } = useParams()
    const { rowRenderer } = useAddDoubleTableGreenRowRender(id || '')
    const { bankRemittanceStatus } = useBankRemittanceStatus()
    const columns: ColumnTableSchema<BankRemittanceModelType> = [
        {
            id: 'id',
            dataKey: 'id',
            width: 200,
            label: t('id', 'ID'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('id', 'ID') }
            }
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            }
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            customRender: (parameterValue) => <DisplayStatus text={bankRemittanceStatus[parseInt(parameterValue) as BankRemittanceStatusKeys].name} colorText={bankRemittanceStatus[parseInt(parameterValue) as BankRemittanceStatusKeys].colorText} icon={bankRemittanceStatus[parseInt(parameterValue) as BankRemittanceStatusKeys].icon} />,
            className: 'overflow-visible w-full',
        },
        {
            id: 'createdAt',
            dataKey: 'createdAt',
            label: t('date', 'Fecha'),
            sortable: true,
            customRender: (parameterValue) => <CustomRenderDate value={parameterValue} />
        },
        {
            id: 'edit',
            dataKey: 'id',
             label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRowNotLink key={'edit' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'edit', allRowData })} />
        }
    ]
    return (
        <TwinTableLink name='remittances' createRowDataLink={[locations.remittances, ['$id']]} columns={columns} getDataFrom='/api/app/accounting/collectionAndPayments/getAllRemittances' setTableInstance={setTableInstance} rowRenderer={rowRenderer} />
    )
}

interface RemittancesPaymentsTableProps extends ComponentWithPermissions { }

const RemittancesPaymentsTable: React.FC<RemittancesPaymentsTableProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('collectionAndPayment', 'Cobros y Pagos'),
        newText: '',
    }

    return (
        <BaseScreenTable TableComponent={TableRemittancesPaymentsTable} CreateModalComponent={NullComponent} EditModalComponent={ModalEditPaymentMethodDate} pageData={pageData} userPermissions={userPermissions} RightHeader={
            <GenerateXML />
        } />
    )
}

interface GenerateXMLProps { }

const GenerateXML: React.FC<GenerateXMLProps> = () => {
    const { generateXML, loading } = useGenerateXMLLogic()
    if (loading) {
        return <div className='w-160'>< LoadingSpinner className='h-24' /></div >
    }
    return (
        <ButtonPrimary className='w-160' onClick={generateXML}>
            <TwinTrans transKey='generateXML'>Generar XML</TwinTrans>
            <a className='hidden downloadfile' download={true} href='/' target='_blank' onClick={(e) => e.stopPropagation()}>a</a>
        </ButtonPrimary>
    )
}

const useGenerateXMLLogic = () => {
    const { startLoading, endLoading, loading } = useIsLoading()
    const { id } = useParams()
    const { t } = useTwinTranslation()
    const remittanceText = t('remittance', 'Remesa')
    const cantGenerateXMLWithoutReceiptSelected = t('cantGenerateXMLWithoutReceiptSelected', 'No puedes generar XML si no hay seleccionados recibos')

    const generateXML = useCallback(async (e: React.MouseEvent) => {
        startLoading?.()
        const current = e.currentTarget

        const result = await twinFetchPost('/api/app/accounting/collectionAndPayments/getXMLForBankRemitttance', { id })
        if (result) {
            if(result.status === 422){
                const responseJSON = await result.json()
                if (responseJSON.errors[0].msg === 'Not receipts in remittance') {
                    changeErrorMessage(cantGenerateXMLWithoutReceiptSelected)
                }
            } else {
                const pdf = await result.blob()
                const fileURL = URL.createObjectURL(pdf)
                const a = current.querySelector('a')
                const fileName = remittanceText + '-' + id
                if (a) {
                    a.href = fileURL
                    a.download = fileName + '.xml'
                    a.click()
                }
            }
        }
        endLoading?.()
    }, [startLoading, endLoading, id, remittanceText, cantGenerateXMLWithoutReceiptSelected])

    return { loading, generateXML }
}



interface TableRemittancesPaymentsTableProps extends TableComponentProps { }

const TableRemittancesPaymentsTable: React.FC<TableRemittancesPaymentsTableProps> = ({ setTableInstance, setOpened }) => {
    const { collectionAndPaymentStatus } = useCollectionAndPaymentStatus()
    const { t } = useTwinTranslation()
    const { id } = useParams()

    const columns: ColumnTableSchema<PayMCopyDateModelTypeComplete> = [
        {
            id: 'date',
            dataKey: 'date',
            label: t('date', 'Fecha'),
            sortable: true,
            customRender: (parameterValue) => <CustomRenderDate value={parameterValue} />
        },
        {
            id: 'CustomerId',
            dataKey: 'PayMCopy.Customer.name',
            label: t('customer', 'Cliente'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customer', 'Cliente') }
            },
            hideColumnByDefault: true
        },
        {
            id: 'full_assigned_number',
            dataKey: 'PayMCopy.SellInvoice.full_assigned_number',
            label: t('invoice', 'Factura'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('invoice', 'Factura') }
            },
        },
        {
            id: 'PayTCopy.name',
            dataKey: 'PayTCopy.name',
            label: t('paymentType', 'Tipo de pago'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('paymentType', 'Tipo de pago') }
            },
        },
        {
            id: 'status',
            dataKey: 'status',
            label: t('status', 'Estado'),
            searchKey: 'PayMCopyDate.status',
            sortable: true,
            customRender: (parameterValue) => <DisplayStatus text={collectionAndPaymentStatus[parseInt(parameterValue) as PayMCopyDateStatusKeys]?.name} colorText={collectionAndPaymentStatus[parseInt(parameterValue) as PayMCopyDateStatusKeys]?.colorText} icon={collectionAndPaymentStatus[parseInt(parameterValue) as PayMCopyDateStatusKeys]?.icon} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelect',
                extraComponentData: { items: collectionAndPaymentStatus, label: t('status', 'Estado') }
            },
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('totalAmount', 'Importe total'),
            customRender: (parameterValue, allRowData) => <CustomRenderDisplayPriceWOParams value={parameterValue} CurrencyId={allRowData?.CurrencyId} />
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'edit', allRowData })} />,
        },
    ]
    return (
        <TwinTable name='remittancesPaymentsTable' columns={columns} getDataFrom='/api/app/accounting/collectionAndPayments/getAllCollectionAndPayments' setTableInstance={setTableInstance} onRowClick={(_idk, allRowData) => setOpened({ type: 'edit', allRowData })} extraWhereParams={{ BankRemittanceId: id }} />
    )
}

export default Remittances